/**
 * Stream animations: segments and islands ASSEMBLE into the void and SINK
 * away, instead of popping. Purely visual, wall-clock-eased, per-piece:
 * a wave of construction flows along the direction of travel (each piece's
 * delay is its projection onto the segment's heading, plus a deterministic
 * per-position jitter). Generation and physics are untouched — the marble
 * only ever rides segments whose spawn wave finished long before it arrives
 * (the render window builds ~3 segments = ~75 units ahead).
 *
 * Two kinds of animated piece:
 * - plain Object3D children (track piece clones) — position/scale eased;
 * - instances inside an InstancedMesh (island blocks/caps/vegetation,
 *   islandRender.ts) — each instance carries a base matrix in
 *   `userData.animInstances` and is eased by rewriting its matrix, so the
 *   per-piece wave survives instancing.
 */
import * as THREE from "three";
import type { AnimInstances } from "./islandRender.ts";

/** Seconds of stagger per unit of distance along the travel direction. */
const STAGGER_PER_UNIT = 0.045;
/** Extra deterministic per-piece jitter (seconds). */
const JITTER = 0.12;
const SPAWN_DURATION = 0.45;
const SPAWN_DROP = 2.0;
const EXIT_DURATION = 0.4;
const EXIT_SINK = 1.6;

interface ObjEntry {
  kind: "obj";
  obj: THREE.Object3D;
  baseY: number;
  baseScale: THREE.Vector3;
  delay: number;
  t: number;
}

interface InstEntry {
  kind: "inst";
  mesh: THREE.InstancedMesh;
  index: number;
  base: THREE.Matrix4;
  delay: number;
  t: number;
}

type Entry = ObjEntry | InstEntry;

const tmpM = new THREE.Matrix4();
const tmpS = new THREE.Matrix4();

function easeOutBack(t: number): number {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
}

function easeInCubic(t: number): number {
  return t * t * t;
}

/** Cheap deterministic hash of a world position → [0, 1). Visual only. */
function posJitter(x: number, y: number, z: number): number {
  let h = Math.imul((x * 4 + 1024) | 0, 374761393) ^
    Math.imul((y * 4 + 2048) | 0, 668265263) ^
    Math.imul((z * 4 + 4096) | 0, 2246822519);
  h = Math.imul(h ^ (h >>> 13), 3266489917);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

function delayFor(
  pos: THREE.Vector3,
  origin: THREE.Vector3,
  heading: THREE.Vector3,
): number {
  const proj = (pos.x - origin.x) * heading.x + (pos.z - origin.z) * heading.z;
  return Math.max(0, proj) * STAGGER_PER_UNIT +
    posJitter(pos.x, pos.y, pos.z) * JITTER;
}

function collectEntries(
  group: THREE.Group,
  origin: THREE.Vector3,
  heading: THREE.Vector3,
): Entry[] {
  const out: Entry[] = [];
  for (const obj of group.children) {
    const anim = (obj as THREE.InstancedMesh).userData
      ?.animInstances as AnimInstances | undefined;
    if (anim && (obj as THREE.InstancedMesh).isInstancedMesh) {
      const mesh = obj as THREE.InstancedMesh;
      for (let i = 0; i < anim.base.length; i++) {
        out.push({
          kind: "inst",
          mesh,
          index: i,
          base: anim.base[i],
          delay: delayFor(anim.worldPos[i], origin, heading),
          t: 0,
        });
      }
    } else {
      out.push({
        kind: "obj",
        obj,
        baseY: obj.position.y,
        baseScale: obj.scale.clone(),
        delay: delayFor(obj.position, origin, heading),
        t: 0,
      });
    }
  }
  return out;
}

/** Write an instance matrix = base offset by dy and scaled by s (about the
 * piece's own origin). */
function writeInstance(e: InstEntry, dy: number, s: number): void {
  tmpM.copy(e.base);
  tmpS.makeScale(s, s, s);
  tmpM.multiply(tmpS);
  tmpM.elements[13] += dy;
  e.mesh.setMatrixAt(e.index, tmpM);
  e.mesh.instanceMatrix.needsUpdate = true;
}

export class SpawnAnim {
  private entries: Entry[];

  constructor(
    group: THREE.Group,
    origin: THREE.Vector3,
    heading: THREE.Vector3,
  ) {
    this.entries = collectEntries(group, origin, heading);
    // First frame state: everything hidden above its target, tiny.
    for (const e of this.entries) {
      if (e.kind === "obj") {
        e.obj.position.y = e.baseY + SPAWN_DROP;
        e.obj.scale.copy(e.baseScale).multiplyScalar(0.001);
      } else {
        writeInstance(e, SPAWN_DROP, 0.001);
      }
    }
  }

  /** Advance; returns true when every piece has settled. */
  update(dt: number): boolean {
    let done = true;
    for (const e of this.entries) {
      e.t += dt;
      const k = (e.t - e.delay) / SPAWN_DURATION;
      if (k <= 0) {
        done = false;
        continue;
      }
      if (k >= 1) {
        if (e.kind === "obj") {
          e.obj.position.y = e.baseY;
          e.obj.scale.copy(e.baseScale);
        } else {
          writeInstance(e, 0, 1);
        }
        continue;
      }
      done = false;
      const f = easeOutBack(k);
      const s = Math.max(0.001, Math.min(1, k * 1.6));
      if (e.kind === "obj") {
        e.obj.position.y = e.baseY + SPAWN_DROP * (1 - f);
        e.obj.scale.copy(e.baseScale).multiplyScalar(s);
      } else {
        writeInstance(e, SPAWN_DROP * (1 - f), s);
      }
    }
    return done;
  }
}

export class ExitAnim {
  private entries: Entry[];

  constructor(
    group: THREE.Group,
    origin: THREE.Vector3,
    heading: THREE.Vector3,
  ) {
    this.entries = collectEntries(group, origin, heading);
  }

  /** Advance; returns true when every piece has sunk away. */
  update(dt: number): boolean {
    let done = true;
    for (const e of this.entries) {
      e.t += dt;
      const k = (e.t - e.delay) / EXIT_DURATION;
      if (k <= 0) {
        done = false;
        continue;
      }
      if (k >= 1) {
        if (e.kind === "obj") e.obj.visible = false;
        else writeInstance(e, -EXIT_SINK, 0.001);
        continue;
      }
      done = false;
      const f = easeInCubic(k);
      if (e.kind === "obj") {
        e.obj.position.y = e.baseY - EXIT_SINK * f;
        e.obj.scale.copy(e.baseScale).multiplyScalar(Math.max(0.001, 1 - f));
      } else {
        writeInstance(e, -EXIT_SINK * f, Math.max(0.001, 1 - f));
      }
    }
    return done;
  }
}
